import React, { ReactNode } from 'react'
import { cn } from '@/utilities/cn'
import { ClassValue } from 'clsx'

export type ContainerMaxWidth = 'prose' | 'narrow' | 'default' | 'wide' | 'full'
export type ContainerPaddingSize = 'none' | 'sm' | 'md' | 'lg' | 'xl'

const MAX_WIDTH_CLASS: Record<ContainerMaxWidth, string> = {
  prose: 'max-w-prose',
  narrow: 'max-w-2xl',
  default: 'max-w-5xl',
  wide: 'max-w-7xl',
  full: 'max-w-none',
}

const PADDING_CLASS: Record<ContainerPaddingSize, string> = {
  none: 'p-0',
  sm: 'p-2',
  md: 'p-4 md:p-6',
  lg: 'p-6 md:p-10',
  xl: 'p-8 md:p-16',
}

export interface ContainerProps {
  children?: ReactNode
  className?: ClassValue
  center?: boolean
  /**
   * Tailwind max-width preset. New canonical field — replaces the legacy
   * boolean `narrow` toggle.
   */
  maxWidth?: string
  /**
   * Padding preset (none/sm/md/lg/xl). New canonical field — replaces the
   * legacy freeform string `padding` (which still works as a fallback when
   * paddingSize is unset, see `legacyPadding`).
   */
  paddingSize?: string
  /**
   * Legacy freeform padding value (raw CSS, e.g. "20px"). Used only when
   * paddingSize is not provided — preserves the rendering of pre-migration
   * documents. Editors should use paddingSize for new content.
   */
  legacyPadding?: string
}

export const Container: React.FC<ContainerProps> = ({
  children,
  className,
  center,
  maxWidth,
  paddingSize,
  legacyPadding,
}) => {
  const widthClass =
    (maxWidth && MAX_WIDTH_CLASS[maxWidth as ContainerMaxWidth]) ?? MAX_WIDTH_CLASS.default
  const paddingClass = paddingSize
    ? PADDING_CLASS[paddingSize as ContainerPaddingSize] ?? PADDING_CLASS.md
    : undefined
  return (
    <div
      className={cn(
        'container',
        center && 'mx-auto',
        widthClass,
        paddingClass,
        className,
      )}
      // legacyPadding wins over paddingSize ONLY when paddingSize is unset
      // — gives existing documents their original look while letting new
      // edits use the structured field.
      style={!paddingSize && legacyPadding ? { padding: legacyPadding } : undefined}
    >
      {children}
    </div>
  )
}
